import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, interval, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { AuthApi } from '../apis/auth.api';
import { RegionRestrictionDialogComponent } from '../components/region-restriction-dialog/region-restriction-dialog.component';
import { MatConnectedDialog, MatConnectedDialogMobileAnimation } from '../service/connected-dialog';
import { LocalStorageService } from './localstorage.service';

// 內部使用的格式
export interface RegionCheckResponse {
  isRestricted: boolean;
  countryCode?: string;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class RegionRestrictionService {
  private readonly _isRestricted = new BehaviorSubject<boolean>(false);
  private readonly _hasChecked = new BehaviorSubject<boolean>(false);
  private readonly _destroy$ = new Subject<void>();
  private _dialogRef: MatDialogRef<RegionRestrictionDialogComponent> | null = null;
  private _periodicCheckSubscription: any = null;

  private _periodicCheckStarted = false;

  // 定期檢查的間隔時間（毫秒）- 每5分鐘檢查一次
  private readonly CHECK_INTERVAL = 5 * 60 * 1000;

  public readonly isRestricted$ = this._isRestricted.asObservable();
  public readonly hasChecked$ = this._hasChecked.asObservable();

  constructor(
    private dialog: MatConnectedDialog,
    private appService: AppService,
    private authApi: AuthApi,
    private localStorageService: LocalStorageService,
  ) {}

  /**
   * 檢查用戶是否在地區限制名單中
   */
  checkRegionRestriction(): Observable<boolean> {
    return this.realRegionCheck().pipe(
      tap((response: RegionCheckResponse) => {
        this._isRestricted.next(response.isRestricted);
        this._hasChecked.next(true);

        if (response.isRestricted) {
          // 如果用戶已登錄但IP被限制，執行登出操作
          const isLoggedIn = this.appService.userInfo$.value !== null;
          if (isLoggedIn) {
            console.log('User is logged in but IP is restricted, performing logout...');
            this.appService.softlyLogout();
          }

          this.showRestrictionDialog(response.countryCode, response.message);
        }
      }),
      map((response: RegionCheckResponse) => response.isRestricted),
      catchError(error => {
        console.error('Region check failed:', error);
        this._hasChecked.next(true);
        // 發生錯誤時默認不限制，避免影響正常用戶
        return of(false);
      }),
    );
  }

  /**
   * 真實API調用
   */
  private realRegionCheck(): Observable<RegionCheckResponse> {
    return this.authApi.checkIpAllowed().pipe(
      map(response => {
        if (!response) {
          return {
            isRestricted: false,
            countryCode: undefined,
            message: 'No response data',
          };
        }

        // 根據 API 規格：如果 success == false 且 code == "401" 則限制訪問
        const isRestricted = !response.success && response.code === '401';

        return {
          isRestricted,
          countryCode: response.data || undefined, // data 欄位包含國家/城市名稱
          message: response.message || undefined,
        };
      }),
      catchError(error => {
        console.error('Region API call failed:', error);
        // API 錯誤時預設不限制
        return of({
          isRestricted: false,
          countryCode: undefined,
          message: 'API check failed',
        });
      }),
    );
  }

  /**
   * 開始定期檢查 IP 限制
   */
  private startPeriodicCheck(): void {
    if (this._periodicCheckStarted) {
      return; // 避免重複啟動
    }

    this._periodicCheckStarted = true;

    // 使用 interval 每隔指定時間檢查一次
    this._periodicCheckSubscription = interval(this.CHECK_INTERVAL)
      .pipe(
        takeUntil(this._destroy$),
        switchMap(() => {
          // 如果已經被限制，停止檢查
          if (this._isRestricted.value) {
            console.log('Already restricted, stopping periodic checks');
            this.stopPeriodicCheck();
            return of(true); // 返回已限制狀態
          }
          return this.checkRegionRestriction();
        }),
      )
      .subscribe({
        next: isRestricted => {
          if (isRestricted) {
            console.log('Periodic check detected region restriction, stopping further checks');
            this.stopPeriodicCheck(); // 停止定期檢查
          } else {
            console.log('Periodic check: region access allowed');
          }
        },
        error: error => {
          console.error('Periodic region check error:', error);
        },
      });
  }

  /**
   * 停止定期檢查
   */
  private stopPeriodicCheck(): void {
    if (this._periodicCheckSubscription) {
      this._periodicCheckSubscription.unsubscribe();
      this._periodicCheckSubscription = null;
      this._periodicCheckStarted = false;
      console.log('Periodic region restriction checks stopped');
    }
  }

  /**
   * 停止所有檢查並清理資源
   */
  destroy(): void {
    this.stopPeriodicCheck();
    this._destroy$.next();
    this._destroy$.complete();
    if (this._dialogRef) {
      this._dialogRef.close();
      this._dialogRef = null;
    }
  }

  /**
   * 顯示地區限制彈窗
   *
   * @param countryCode
   * @param apiMessage
   */
  private showRestrictionDialog(countryCode?: string, apiMessage?: string): void {
    if (this._dialogRef) {
      return; // 已經顯示彈窗，避免重複
    }

    // 動態生成訊息內容
    // 翻譯於彈窗顯示時進行（此處可能早於語系資料載入）
    const message = countryCode
      ? 'due_to_gaming_license_cannot_accept_players_from'
      : 'due_to_gaming_license_cannot_accept_players_from_your_location';

    this._dialogRef = this.dialog.open(RegionRestrictionDialogComponent, {
      data: {
        title: 'you_are_unable_to_access_brand_from_your_location',
        message: [message, 'if_using_vpn_please_disable_and_try_again'],
        messageParams: [countryCode ? [countryCode] : []],
        confirmText: 'reload_page',
        cancelText: 'contact_support_2',
        translate: true,
        closeOnCancel: false, // 點擊Contact Support按鈕後不關閉彈窗
        onConfirm: () => {
          this.handleReloadPage();
        },
        onCancel: () => {
          this.handleContactSupport();
        },
      },
      disableClose: true,
      closeOnNavigation: false,
      hasBackdrop: true,
      panelClass: 'region-restriction-dialog',
      mobileAnimation: MatConnectedDialogMobileAnimation.SLIDE_UP,
      mobileFixedBottom: true,
    });

    // 監聽彈窗關閉事件
    this._dialogRef.afterClosed().subscribe(() => {
      this._dialogRef = null;
    });
  }

  /**
   * 處理重新載入頁面
   */
  private handleReloadPage(): void {
    window.location.reload();
  }

  /**
   * 處理聯繫客服
   */
  private handleContactSupport(): void {
    this.appService.toOnLineService$.next(true);
  }

  /**
   * 獲取當前限制狀態
   */
  get isRestricted(): boolean {
    return this._isRestricted.value;
  }

  /**
   * 獲取是否已檢查過
   */
  get hasChecked(): boolean {
    return this._hasChecked.value;
  }

  /**
   * 應用初始化時執行的函數 (APP_INITIALIZER)
   */
  initialize(): Promise<boolean> {
    return new Promise(resolve => {
      // 在應用啟動時檢查地區限制
      this.checkRegionRestriction().subscribe({
        next: isRestricted => {
          console.log('Region restriction check completed:', isRestricted ? 'RESTRICTED' : 'ALLOWED');

          // 啟動定期檢查（不管用戶是否登錄）
          this.startPeriodicCheck();

          resolve(true);
        },
        error: error => {
          console.error('Region restriction check failed:', error);

          // 即使檢查失敗也要啟動定期檢查
          this.startPeriodicCheck();

          // 即使檢查失敗也要繼續啟動應用
          resolve(true);
        },
      });
    });
  }
}
